import { KeyboardArrowRight } from '@mui/icons-material';
import { ListItemButton, ListItemIcon, Stack, Typography } from '@mui/material';
import { useWorkspaceId } from 'api/providers/workspaceProvider';
import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FinsightEntity } from 'shared';
import { formatName } from 'utils/fields';

export function SearchResultEntityListItems(props: {
  entities: FinsightEntity[];
  onClick: (open: boolean, e?: FinsightEntity) => void;
  overrideClick?: boolean;
}) {
  const { entities: data, onClick: setOpen, overrideClick } = props;
  const workspaceId = useWorkspaceId();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const focusRef = useRef<HTMLAnchorElement>(null);

  const [_focus, setFocus] = useState(0);

  const focus = _focus >= 0 && _focus < data.length ? _focus : 0;

  useEffect(() => {
    const func = async (e: KeyboardEvent) => {
      if (!data.length) return;
      if (e.key === 'Enter') {
        setOpen(false, data[focus]);
        if (!overrideClick) {
          navigate(
            `/workspace/${workspaceId}/entity/${data[focus].id}?${searchParams.toString()}`,
          );
        }
      }
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        const nextFocus = Math.min(focus + 1, data.length - 1);
        setFocus(nextFocus);
        focusRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        const nextFocus = Math.max(focus - 1, 0);
        setFocus(nextFocus);

        focusRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'center',
        });
      }
    };
    window.addEventListener('keydown', func);
    return () => {
      window.removeEventListener('keydown', func);
    };
  }, [
    data,
    data.length,
    focus,
    navigate,
    overrideClick,
    searchParams,
    setOpen,
    workspaceId,
  ]);

  return data?.map((entity, i) => (
    <ListItemButton
      ref={i === focus ? focusRef : null}
      selected={i === focus}
      key={entity.id}
      href={
        overrideClick
          ? (undefined as any)
          : `/workspace/${workspaceId}/entity/${entity.id}?${searchParams.toString()}`
      }
      onClick={(e) => {
        if (e.metaKey || e.ctrlKey) {
          return;
        }
        setOpen(false, entity);
      }}
    >
      <Stack flexGrow={1}>
        <Typography
          color="text.secondary"
          textTransform="uppercase"
          fontSize={11}
          letterSpacing={2}
          pr={1}
          minWidth={80}
        >
          {formatName(entity.types?.[0])}
        </Typography>
        <Typography variant="h6">{entity.name}</Typography>
        <Typography>{entity.description}</Typography>
      </Stack>
      <ListItemIcon>
        <KeyboardArrowRight />
      </ListItemIcon>
    </ListItemButton>
  ));
}
